import { APP_ENV } from '@env';
import { useNavigation } from '@react-navigation/native';
import { Box, Button, Input, Text, VStack } from 'native-base';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { testContextActions } from '../../store/actions';

const TestModeScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation();
  const { testDate, loading, error } = useAppSelector((state) => state.testContext);
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (testDate) {
      setInputValue(testDate);
    }
  }, [testDate]);

  if (APP_ENV === 'production') return null;

  const handleSet = async () => {
    setValidationError(null);
    const parsed = new Date(inputValue);
    if (isNaN(parsed.getTime())) {
      setValidationError('Enter a valid date, e.g. 2026-01-15 or 2026-01-15T10:00:00Z');
      return;
    }
    try {
      await dispatch(testContextActions.setTestDate(parsed.toISOString())).unwrap();
      navigation.goBack();
    } catch {
      // error is written to state.error by the slice's rejected handler
    }
  };

  const handleClear = async () => {
    try {
      await dispatch(testContextActions.clearTestDate()).unwrap();
      setInputValue('');
      navigation.goBack();
    } catch {
      // error is written to state.error by the slice's rejected handler
    }
  };

  return (
    <Box flex={1} safeArea px={6} py={8} bg="white">
      <VStack space={4}>
        <Text fontSize={16} fontWeight="bold">
          Test Date Context
        </Text>
        <Text fontSize={13} color="gray.500">
          Sets a simulated "now" for your session. The app will behave as if it is currently this
          date — on both the server and client. Non-production environments only.
        </Text>

        {testDate ? (
          <Box bg="amber.100" borderRadius={8} p={3}>
            <Text fontSize={12} color="amber.900" fontWeight="bold">
              Active:{' '}
              {new Date(testDate).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          </Box>
        ) : (
          <Box bg="gray.100" borderRadius={8} p={3}>
            <Text fontSize={12} color="gray.500">
              Test mode is off. Real date is in use.
            </Text>
          </Box>
        )}

        <Input
          value={inputValue}
          onChangeText={setInputValue}
          placeholder="2026-01-15T10:00:00Z"
          autoCapitalize="none"
          autoCorrect={false}
          fontSize={14}
        />

        {validationError ? (
          <Text fontSize={12} color="red.500">
            {validationError}
          </Text>
        ) : null}

        {error ? (
          <Text fontSize={12} color="red.500">
            {error}
          </Text>
        ) : null}

        <Button onPress={handleSet} isLoading={loading} isDisabled={!inputValue.trim()}>
          Set Test Date
        </Button>

        {testDate ? (
          <Button variant="outline" colorScheme="gray" onPress={handleClear} isLoading={loading}>
            Clear Test Date
          </Button>
        ) : null}
      </VStack>
    </Box>
  );
};

export default TestModeScreen;
